import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { IExtensionsWorkbenchService } from 'vscode/vscode/vs/workbench/contrib/extensions/common/extensions.service';
import { IExtensionService } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions.service';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { MenuRegistry, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { areSameExtensions } from 'vscode/vscode/vs/platform/extensionManagement/common/extensionManagementUtil';
import 'vscode/vscode/vs/platform/notification/common/notification';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { Action } from 'vscode/vscode/vs/base/common/actions';
import { IHostService } from 'vscode/vscode/vs/workbench/services/host/browser/host.service';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { Promises } from 'vscode/vscode/vs/base/common/async';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';

const _moduleId = "vs/workbench/contrib/extensions/browser/extensionsDependencyChecker";
let ExtensionDependencyChecker = class ExtensionDependencyChecker extends Disposable {
    constructor(extensionService, extensionsWorkbenchService, notificationService, hostService) {
        super();
        this.extensionService = extensionService;
        this.extensionsWorkbenchService = extensionsWorkbenchService;
        this.notificationService = notificationService;
        this.hostService = hostService;
        CommandsRegistry.registerCommand('workbench.extensions.installMissingDependencies', () => this.installMissingDependencies());
        MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
            command: {
                id: 'workbench.extensions.installMissingDependencies',
                category: ( localizeWithPath(_moduleId, 0, "Extensions")),
                title: ( localizeWithPath(_moduleId, 1, "Install Missing Dependencies"))
            }
        });
    }
    async getUninstalledMissingDependencies() {
        const allMissingDependencies = await this.getAllMissingDependencies();
        const localExtensions = await this.extensionsWorkbenchService.queryLocal();
        return allMissingDependencies.filter(id => localExtensions.every(l => !areSameExtensions(l.identifier, { id })));
    }
    async getAllMissingDependencies() {
        await this.extensionService.whenInstalledExtensionsRegistered();
        const runningExtensionsIds = this.extensionService.extensions.reduce((result, r) => { result.add(r.identifier.value.toLowerCase()); return result; }, ( (new Set())));
        const missingDependencies = ( (new Set()));
        for (const extension of this.extensionService.extensions) {
            if (extension.extensionDependencies) {
                extension.extensionDependencies.forEach(dep => {
                    if (!( (runningExtensionsIds.has(dep.toLowerCase())))) {
                        missingDependencies.add(dep);
                    }
                });
            }
        }
        return [...( (missingDependencies.values()))];
    }
    async installMissingDependencies() {
        const missingDependencies = await this.getUninstalledMissingDependencies();
        if (missingDependencies.length) {
            const extensions = await this.extensionsWorkbenchService.getExtensions(( (missingDependencies.map(id => ({ id })))), CancellationToken.None);
            if (extensions.length) {
                await Promises.settled(( (extensions.map(extension => this.extensionsWorkbenchService.install(extension)))));
                this.notificationService.notify({
                    severity: Severity$1.Info,
                    message: ( localizeWithPath(
                        _moduleId,
                        2,
                        "Finished installing missing dependencies. Please reload the window now."
                    )),
                    actions: {
                        primary: [( (new Action('realod', ( localizeWithPath(_moduleId, 3, "Reload Window")), '', true, () => this.hostService.reload())))]
                    }
                });
            }
        }
        else {
            this.notificationService.info(( localizeWithPath(_moduleId, 4, "There are no missing dependencies to install.")));
        }
    }
};
ExtensionDependencyChecker = ( (__decorate([
    ( (__param(0, IExtensionService))),
    ( (__param(1, IExtensionsWorkbenchService))),
    ( (__param(2, INotificationService))),
    ( (__param(3, IHostService)))
], ExtensionDependencyChecker)));

export { ExtensionDependencyChecker };
